import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../api/client';

/**
 * Features map returned from the license service.
 * Keys are capability identifiers like "FSC:scan", "FSC:userprofile", etc.
 * Values are booleans indicating if the feature is active for the tenant.
 */
export interface Features {
  [key: string]: boolean;
}

interface FeaturesContextValue {
  features: Features;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const FeaturesContext = createContext<FeaturesContextValue | null>(null);

interface FeaturesProviderProps {
  children: ReactNode;
}

export const FeaturesProvider = ({ children }: FeaturesProviderProps) => {
  const [features, setFeatures] = useState<Features>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFeatures = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await api.get<Features>('/features');
      setFeatures(data);
    } catch (err: any) {
      const message = err.message || 'Failed to load features';
      setError(message);
      console.error('Failed to load features:', err);
      // Keep features disabled on error (empty object = all features disabled)
      setFeatures({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, []);

  return (
    <FeaturesContext.Provider
      value={{
        features,
        loading,
        error,
        refresh: fetchFeatures,
      }}
    >
      {children}
    </FeaturesContext.Provider>
  );
};

export const useFeatures = (): FeaturesContextValue => {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeaturesProvider');
  }
  return context;
};
